import React from "react";
import { db } from "@/lib/firebase";
import { collection, doc, onSnapshot, orderBy, query, updateDoc, where, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import useUserProfile from "@/hooks/useUserProfile";
import useCoopMemberRole from "@/hooks/useCoopMemberRole";
import { ToastContext } from "@/components/ToastProvider.jsx";
import { formatDT } from "@/utils/dates";
import CodRemittanceModal from "@/components/CodRemittanceModal";
import { 
  Package, 
  User, 
  MapPin, 
  Truck, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Phone,
  Calendar,
  DollarSign,
  Eye
} from "lucide-react";

function peso(n){ return Number(n||0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2}); }

// Smart status progression - only show relevant next actions
const getNextStatusOptions = (currentStatus) => {
  const statusFlow = {
    'queued': ['assigned', 'canceled'],
    'assigned': ['out_for_delivery', 'canceled'],
    'out_for_delivery': ['completed', 'canceled'],
    'completed': [],
    'canceled': [],
    'remittance_pending': ['completed', 'canceled']
  };
  return statusFlow[currentStatus] || [];
};

// Status display configuration
const getStatusConfig = (status) => {
  const configs = {
    'queued': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Queued' },
    'assigned': { color: 'bg-blue-100 text-blue-800', icon: User, label: 'Assigned' },
    'out_for_delivery': { color: 'bg-purple-100 text-purple-800', icon: Truck, label: 'Out for Delivery' },
    'for_review': { color: 'bg-orange-100 text-orange-800', icon: Eye, label: 'For Review' },
    'remittance_pending': { color: 'bg-amber-100 text-amber-800', icon: DollarSign, label: 'Remittance Pending' },
    'handover_confirmed': { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle, label: 'Handover Confirmed' },
    'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
    'canceled': { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Canceled' }
  };
  return configs[status] || configs['queued'];
};

// Enhanced rider display component
function RiderInfo({ riderId, ridersByUid, assignedAt, showContact = true, compact = false }) {
  const rider = ridersByUid[riderId];
  
  if (!riderId) {
    return (
      <div className="text-sm text-gray-500">
        <span className="inline-flex items-center gap-1">
          <User className="w-3 h-3" />
          Unassigned
        </span>
      </div>
    );
  }

  if (!rider) {
    return (
      <div className="text-sm text-gray-900">
        <span className="inline-flex items-center gap-1">
          <User className="w-3 h-3" />
          {riderId.slice(0, 8)}...
        </span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="text-sm">
        <div className="font-medium text-gray-900 truncate" title={rider.name || 'Unknown Rider'}>
          {rider.name || 'Unknown Rider'}
        </div>
        {showContact && rider.phone && (
          <div className="text-xs text-gray-600 truncate" title={rider.phone}>
            📞 {rider.phone}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
        <User className="w-3 h-3" />
        <span className="truncate" title={rider.name || 'Unknown Rider'}>
          {rider.name || 'Unknown Rider'}
        </span>
      </div>
      {showContact && rider.phone && (
        <div className="text-xs text-gray-600 flex items-center gap-1">
          <Phone className="w-3 h-3" />
          <span className="truncate" title={rider.phone}>
            {rider.phone}
          </span>
        </div>
      )}
      {assignedAt && (
        <div className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          <span className="truncate" title={formatDT(assignedAt.toDate ? assignedAt.toDate() : new Date(assignedAt))}>
            {formatDT(assignedAt.toDate ? assignedAt.toDate() : new Date(assignedAt))}
          </span>
        </div>
      )}
    </div>
  );
}

export default function DeliveriesAdmin(){
  const { profile } = useUserProfile();
  const { isFinancial } = useCoopMemberRole();
  const isStaff = isFinancial;
  const { notify } = React.useContext(ToastContext);
  const [activeTab, setActiveTab] = React.useState('deliveries'); // deliveries | pickup
  const [rows, setRows] = React.useState([]);
  const [riders, setRiders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [expandedDelivery, setExpandedDelivery] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [remittanceModal, setRemittanceModal] = React.useState({ open: false, delivery: null });
  // Completed pickup orders state
  const [pickupRows, setPickupRows] = React.useState([]);
  const [pickupLoading, setPickupLoading] = React.useState(true);
  const [pickupErr, setPickupErr] = React.useState("");

  const ridersByUid = React.useMemo(()=>{
    const map = Object.create(null);
    (riders||[]).forEach((u)=>{ if (u?.uid) map[u.uid] = u; });
    return map;
  }, [riders]);

  React.useEffect(()=>{
    setLoading(true);
    try{
      const q = query(collection(db, "deliveries"), orderBy("status","asc"), orderBy("createdAt","desc"));
      const unsub = onSnapshot(q, (snap)=>{ setRows(snap.docs.map(d=>({ id:d.id, ...d.data()}))); setLoading(false); }, (e)=>{ setErr(e?.message||String(e)); setLoading(false); });
      return ()=>unsub();
    }catch(e){ setErr(String(e)); setLoading(false); }
  }, []);

  // Completed pickups are now shown under Pickup sub-filter in dashboard
  React.useEffect(()=>{
    setPickupLoading(true);
    try{
      // method stored as 'pickup' for pickup orders, status completed
      let q = query(
        collection(db, "orders"),
        where("method", "==", "pickup"),
        where("status", "==", "completed"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const unsub = onSnapshot(q, (snap)=>{
        setPickupRows(snap.docs.map(d=>({ id:d.id, ...d.data() })));
        setPickupLoading(false);
      }, (e)=>{ setPickupErr(e?.message||String(e)); setPickupLoading(false); });
      return ()=>unsub();
    }catch(e){ setPickupErr(String(e)); setPickupLoading(false); }
  }, []);

  React.useEffect(()=>{
    async function load(){
      try{ const fn = httpsCallable(functions, "listRiders"); const res = await fn(); setRiders(Array.isArray(res?.data?.riders)? res.data.riders : []); }catch{}
    }
    load();
  }, []);

  async function assign(row, riderUid){
    try{ 
      const fn = httpsCallable(functions, "assignDelivery"); 
      await fn({ orderId: row.orderId, deliveryId: row.id, riderUid }); 
      notify("Rider assigned successfully.", { type: "success" }); 
    }catch(e){ 
      notify(e?.message||String(e), { type: "error" }); 
    }
  }

  async function setStatus(row, next){
    try{ 
      await updateDoc(doc(db, "deliveries", row.id), { status: next }); 
      notify(`Status updated to ${next}.`, { type: "success" }); 
    }catch(e){ 
      notify(e?.message||String(e), { type: "error" }); 
    }
  }

  // New function to handle remittance modal
  function openRemittanceModal(delivery) {
    setRemittanceModal({ 
      open: true, 
      delivery: {
        deliveryId: delivery.id,
        orderId: delivery.orderId || delivery.id,
        // PRIORITY: Use stored codAmount from database first, fallback to subtotal only if needed
        codAmount: delivery.codAmount !== undefined && delivery.codAmount !== null ? delivery.codAmount : delivery.subtotal,
        customerName: delivery.buyerName || delivery.customerName,
        customerAddress: delivery.address,
        customerPhone: delivery.phone
      }
    });
  }

  function closeRemittanceModal() {
    setRemittanceModal({ open: false, delivery: null });
  }

  // Filter deliveries based on status
  const filteredRows = React.useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter(row => row.status === statusFilter);
  }, [rows, statusFilter]);

  // Get status counts for filter tabs
  const statusCounts = React.useMemo(() => {
    const counts = { all: rows.length };
    rows.forEach(row => {
      counts[row.status] = (counts[row.status] || 0) + 1;
    });
    return counts;
  }, [rows]);

  if (!isStaff) return <div className="card p-4">You do not have access.</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Deliveries Management</h2>
          <p className="text-gray-600 mt-1">Monitor and manage delivery operations</p>
        </div>
        <div className="text-sm text-gray-500">
          {activeTab === 'deliveries' ? (
            <>Total: {rows.length} • Active: {rows.filter(r => !['completed', 'canceled'].includes(r.status)).length}</>
          ) : (
            <>Pickup Orders: {pickupRows.length}</>
          )}
        </div>
      </div>

      {/* Top-level tabs */}
      <div className="border-b border-gray-200">
        <div className="flex flex-wrap gap-2 py-2">
          <button
            onClick={()=>setActiveTab('deliveries')}
            className={`py-2 px-3 border-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'deliveries' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Active Deliveries
            <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">{rows.length}</span>
          </button>
          <button
            onClick={()=>setActiveTab('pickup')}
            className={`py-2 px-3 border-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'pickup' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Pickup Orders
            <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">{pickupRows.length}</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {activeTab === 'deliveries' && err && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{err}</span>
          </div>
        </div>
      )}
  {activeTab === 'pickup' && pickupErr && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{pickupErr}</span>
          </div>
        </div>
      )}

      {/* Status Filter Tabs - only for active deliveries */}
      {activeTab === 'deliveries' && (
        <div className="border-b border-gray-200">
          <div className="flex flex-wrap gap-2 py-2">
            {Object.entries(statusCounts).map(([status, count]) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`py-2 px-3 border-2 rounded-lg font-medium text-sm transition-colors ${
                  statusFilter === status
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {status === 'all' ? 'All' : getStatusConfig(status).label}
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active Deliveries List */}
      {activeTab === 'deliveries' && (loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading deliveries...</p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No deliveries found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop Table */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto max-w-full">
                <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rider Assignment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">COD</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remittance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRows.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">#{row.orderId || '—'}</div>
                        <div className="text-sm text-gray-500">{row.createdAt?.toDate ? formatDT(row.createdAt.toDate()) : '-'}</div>
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded mt-1">
                            ⚠️ Remittance Pending
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{row.buyerName || '—'}</div>
                        <div className="text-sm text-gray-500">{row.buyerUid || '—'}</div>
                        {row.status === 'remittance_pending' && row.totalRemitted && (
                          <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded mt-1">
                            ₱{Number(row.totalRemitted).toFixed(2)} of ₱{Number(row.codAmount || 0).toFixed(2)} remitted
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={row.address || ''}>
                          {row.address || '—'}
                        </div>
                        {row.status === 'remittance_pending' && row.remittanceCount && (
                          <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded mt-1">
                            {row.remittanceCount}/3 remittances
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <RiderInfo 
                          riderId={row.riderId} 
                          ridersByUid={ridersByUid} 
                          assignedAt={row.assignedAt}
                          showContact={true}
                          compact={true}
                        />
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded mt-1">
                            ⚠️ Remittance Required
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const config = getStatusConfig(row.status);
                          const Icon = config.icon;
                          return (
                            <div className="space-y-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                                <Icon className="w-3 h-3 mr-1" />
                                {config.label}
                              </span>
                              {row.status === 'remittance_pending' && row.remittanceCount && (
                                <div className="text-xs text-gray-500">
                                  {row.remittanceCount}/3 remittances
                                </div>
                              )}
                              {row.status === 'remittance_pending' && row.totalRemitted && (
                                <div className="text-xs text-amber-600">
                                  ₱{Number(row.totalRemitted).toFixed(2)} remitted
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="text-right">
                          {row.codAmount ? `₱${peso(row.codAmount)}` : '—'}
                          {row.status === 'remittance_pending' && row.totalRemitted && (
                            <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded mt-1">
                              ₱{Number(row.totalRemitted).toFixed(2)} remitted
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {row.status === 'remittance_pending' && row.totalRemitted ? (
                          <div className="text-xs">
                            <div className="text-amber-600 font-medium">
                              ₱{Number(row.totalRemitted).toFixed(2)} of ₱{Number(row.codAmount || 0).toFixed(2)}
                            </div>
                            <div className="text-gray-500">
                              {row.remittanceCount || 0}/3 remittances
                            </div>
                          </div>
                        ) : row.status === 'completed' ? (
                          <div className="text-xs text-green-600 font-medium">Completed</div>
                        ) : (
                          <div className="text-xs text-gray-500">—</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex flex-col gap-2">
                          <select 
                            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                            value={row.riderId || ''} 
                            onChange={(e) => assign(row, e.target.value || null)}
                          >
                            <option value="">Assign Rider</option>
                            {riders.map(u => (
                              <option key={u.uid} value={u.uid}>
                                {u.name} {u.phone ? `(${u.phone})` : ''}
                              </option>
                            ))}
                          </select>
                          
                          {/* COD Remittance Button */}
                          {row.status === 'remittance_pending' && (
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => openRemittanceModal(row)}
                            >
                              Process Remittance
                            </button>
                          )}
                          
                          {getNextStatusOptions(row.status).length > 0 && (
                            <select 
                              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 w-full"
                              onChange={(e) => setStatus(row, e.target.value)}
                              defaultValue=""
                            >
                              <option value="">Update Status</option>
                              {getNextStatusOptions(row.status).map(status => (
                                <option key={status} value={status}>
                                  {getStatusConfig(status).label}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                </div>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {filteredRows.map(row => {
              const isExpanded = expandedDelivery === row.id;
              const config = getStatusConfig(row.status);
              const Icon = config.icon;
              const nextStatusOptions = getNextStatusOptions(row.status);
              
              return (
                <div key={row.id} className="card p-4">
                  {/* Card Header */}
                  <div className="mb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            Order #{row.orderId || '—'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {row.createdAt?.toDate ? formatDT(row.createdAt.toDate()) : '-'}
                        </div>
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded mb-2">
                            ⚠️ Remittance Pending - ₱{Number(row.totalRemitted || 0).toFixed(2)} of ₱{Number(row.codAmount || 0).toFixed(2)} collected
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded mb-2">
                            📋 {row.remittanceCount || 0}/3 remittances submitted
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded mb-2">
                            💰 ₱{Number((row.codAmount || 0) - (row.totalRemitted || 0)).toFixed(2)} remaining to collect
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded mb-2">
                            🎯 Target: Complete remittance to finish delivery
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-orange-700 bg-orange-100 px-2 py-1 rounded mb-2">
                            ⏰ Remittance deadline: 3 attempts maximum
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-indigo-700 bg-indigo-100 px-2 py-1 rounded mb-2">
                            🔄 Status: Awaiting cashier/treasurer action
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded mb-2">
                            🚨 Action Required: Process remittance to complete delivery
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded mb-2">
                            💡 Tip: Use the "Process Remittance" button below
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-pink-700 bg-pink-100 px-2 py-1 rounded mb-2">
                            🎉 Bonus: Rider earns ₱45 + 90% overweight fee upon completion
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-teal-700 bg-teal-100 px-2 py-1 rounded mb-2">
                            📊 Accounting: Will be posted as operating expense
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-cyan-700 bg-cyan-100 px-2 py-1 rounded mb-2">
                            🔐 Security: Only cashiers/treasurers can process remittances
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-lime-700 bg-lime-100 px-2 py-1 rounded mb-2">
                            🎯 Goal: Complete delivery and credit rider wallet
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-emerald-700 bg-emerald-100 px-2 py-1 rounded mb-2">
                            🚀 Next: Click "Process Remittance" to continue
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-violet-700 bg-violet-100 px-2 py-1 rounded mb-2">
                            🎭 Role: You are acting as cashier/treasurer
                          </div>
                        )}
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-rose-700 bg-rose-100 px-2 py-1 rounded mb-2">
                            🎯 Final: Complete remittance to finish delivery
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {config.label}
                          </span>
                          {row.codAmount && (
                            <span className="text-sm font-medium text-gray-900">
                              COD: ₱{peso(row.codAmount)}
                            </span>
                          )}
                          {row.status === 'remittance_pending' && row.totalRemitted && (
                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                              ₱{Number(row.totalRemitted).toFixed(2)} of ₱{Number(row.codAmount || 0).toFixed(2)} remitted
                            </span>
                          )}
                          {row.status === 'remittance_pending' && row.remittanceCount && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {row.remittanceCount}/3 remittances
                            </span>
                          )}
                        </div>
                        {row.status === 'remittance_pending' && (
                          <div className="text-xs text-gray-600 mt-1">
                            Remittance progress: {row.remittanceCount || 0}/3 remittances
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setExpandedDelivery(isExpanded ? null : row.id)}
                        className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                      >
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="pt-4 border-t border-gray-200 space-y-4">
                      {/* Customer Info */}
                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Customer:</span>
                          <span className="text-sm font-medium">{row.buyerName || row.buyerUid || '—'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                          <span className="text-sm text-gray-600">Address:</span>
                          <span className="text-sm font-medium flex-1">{row.address || '—'}</span>
                        </div>
                      </div>

                      {/* Enhanced Rider Info */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                          <Truck className="w-4 h-4 text-blue-600" />
                          Rider Assignment
                        </h4>
                        <RiderInfo 
                          riderId={row.riderId} 
                          ridersByUid={ridersByUid} 
                          assignedAt={row.assignedAt}
                          showContact={true}
                          compact={false}
                        />
                      </div>

                      {/* Remittance Info */}
                      {row.status === 'remittance_pending' && (
                        <div className="bg-amber-50 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-amber-600" />
                            COD Remittance Status
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-amber-700">Total COD:</span>
                              <span className="font-medium">₱{Number(row.codAmount || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-amber-700">Total Remitted:</span>
                              <span className="font-medium">₱{Number(row.totalRemitted || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-amber-700">Remaining:</span>
                              <span className="font-medium">₱{Number((row.codAmount || 0) - (row.totalRemitted || 0)).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-amber-700">Remittance Count:</span>
                              <span className="font-medium">{row.remittanceCount || 0}/3</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="space-y-3">
                        {/* Rider Assignment */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Assign Rider</label>
                          <select 
                            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={row.riderId || ''} 
                            onChange={(e) => assign(row, e.target.value || null)}
                          >
                            <option value="">Select a rider</option>
                            {riders.map(u => (
                              <option key={u.uid} value={u.uid}>
                                {u.name} {u.phone ? `(${u.phone})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* COD Remittance */}
                        {row.status === 'remittance_pending' && (
                          <div>
                            <button 
                              className="w-full btn btn-primary"
                              onClick={() => openRemittanceModal(row)}
                            >
                              Process Remittance
                            </button>
                            {row.totalRemitted && (
                              <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mt-2 text-center">
                                ₱{Number(row.totalRemitted).toFixed(2)} of ₱{Number(row.codAmount || 0).toFixed(2)} remitted
                              </div>
                            )}
                          </div>
                        )}

                        {/* Status Update */}
                        {nextStatusOptions.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Update Status</label>
                            <select 
                              className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onChange={(e) => setStatus(row, e.target.value)}
                              defaultValue=""
                            >
                              <option value="">Select new status</option>
                              {nextStatusOptions.map(status => (
                                <option key={status} value={status}>
                                  {getStatusConfig(status).label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Completed Pickup Orders List */}
  {activeTab === 'pickup' && (pickupLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading completed pickup orders...</p>
        </div>
      ) : pickupRows.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No completed pickup orders found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop Table for Pickup Orders */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto max-w-full">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pickupRows.map(row => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">#{row.orderId || row.id}</div>
                          <div className="text-sm text-gray-500">{row.createdAt?.toDate ? formatDT(row.createdAt.toDate()) : '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{row.buyerName || row.memberName || '—'}</div>
                          <div className="text-xs text-gray-500">{row.userId || row.memberUid || ''}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {Array.isArray(row.items) ? row.items.length : (row.itemCount || '—')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {row.grandTotal !== undefined ? `₱${peso(row.grandTotal)}` : (row.total !== undefined ? `₱${peso(row.total)}` : '—')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {row.completedAt?.toDate ? formatDT(row.completedAt.toDate()) : (row.updatedAt?.toDate ? formatDT(row.updatedAt.toDate()) : '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Cards for Pickup Orders */}
          <div className="lg:hidden space-y-3">
            {pickupRows.map(row => (
              <div key={row.id} className="card p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">Order #{row.orderId || row.id}</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-2">{row.createdAt?.toDate ? formatDT(row.createdAt.toDate()) : '-'}</div>
                    <div className="text-sm text-gray-900">{row.buyerName || row.memberName || '—'}</div>
                    <div className="text-xs text-gray-600">Items: {Array.isArray(row.items) ? row.items.length : (row.itemCount || '—')}</div>
                    <div className="text-sm font-medium text-gray-900 mt-2">{row.grandTotal !== undefined ? `₱${peso(row.grandTotal)}` : (row.total !== undefined ? `₱${peso(row.total)}` : '—')}</div>
                    <div className="text-xs text-gray-600">Completed: {row.completedAt?.toDate ? formatDT(row.completedAt.toDate()) : (row.updatedAt?.toDate ? formatDT(row.updatedAt.toDate()) : '-')}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* COD Remittance Modal */}
      {remittanceModal.open && (
        <CodRemittanceModal
          isOpen={remittanceModal.open}
          onClose={closeRemittanceModal}
          deliveryData={remittanceModal.delivery}
          onSuccess={() => {
            closeRemittanceModal();
            notify("COD remittance processed successfully!", { type: "success" });
          }}
        />
      )}
    </div>
  );
}
